import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { ShieldCheck, Loader2, CheckCircle2, ArrowRight, Sparkles, AlertTriangle } from "lucide-react";
import { branding } from "@/config/branding";
import { Button, Input, Label, toast } from "@/components/ds";
import { api, formatApiError } from "@/lib/api";
import { BrandLogo } from "@/components/BrandLogo";

const money = (n: number | null | undefined) => (n == null ? "—" : `$${n.toLocaleString()}`);

export default function CustomerActivation() {
  const { token = "" } = useParams();
  const [params] = useSearchParams();
  const returnedPaid = params.get("paid") === "1";

  const [info, setInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(returnedPaid);

  // Set-password form
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const load = useCallback(async () => {
    try {
      const data = await api.activationGet(token);
      setInfo(data);
      setFirstName((p) => p || data.first_name || "");
      setError(null);
      return data;
    } catch (err) {
      setError(formatApiError(err));
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // After returning from Stripe, poll until the backend confirms payment.
  useEffect(() => {
    if (!returnedPaid) return;
    let tries = 0;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const s = await api.activationStatus(token);
        if (s.paid) { await load(); setConfirming(false); return; }
      } catch { /* keep trying */ }
      tries += 1;
      if (tries >= 15) { await load(); setConfirming(false); return; }
      timer = setTimeout(poll, 2500);
    };
    poll();
    return () => clearTimeout(timer);
  }, [returnedPaid, token, load]);

  const pay = async () => {
    setBusy(true);
    try {
      const res = await api.activationCheckout(token);
      window.location.href = res.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8 || password !== confirm) {
      toast.error("Passwords must match and be at least 8 characters."); return;
    }
    setBusy(true);
    try {
      const res = await api.activationSetPassword(token, { password, first_name: firstName, last_name: lastName });
      localStorage.setItem("resfil_token", res.token);
      toast.success("Your account is ready — welcome to ResFil AI!");
      window.location.href = res.redirect_to || "/app/dashboard";
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  const plan = info?.plan;
  const paid = !!info?.paid;
  const passwordSet = !!info?.password_set;
  const expired = !!info?.expired;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-16 max-w-4xl items-center px-4 sm:px-6">
          <Link to="/" className="flex items-center" data-testid="activation-logo">
            <BrandLogo className="h-8" />
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-12 sm:px-6" data-testid="customer-activation">
        {loading ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : error ? (
          <InvalidCard message={error} />
        ) : passwordSet ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm" data-testid="activation-already-setup">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
            <h1 className="mt-4 font-display text-2xl font-semibold text-slate-900">Your account is already set up</h1>
            <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">Please sign in to access your ResFil AI dashboard.</p>
            <Link to="/login" className="mt-6 inline-block"><Button data-testid="activation-goto-login">Go to sign in <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
          </div>
        ) : confirming ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm" data-testid="activation-confirming">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-600" />
            <p className="mt-4 text-sm text-slate-500">Confirming your payment with Stripe…</p>
          </div>
        ) : paid ? (
          /* ---------- Step 2: Set your password ---------- */
          <div className="mx-auto max-w-md">
            <div className="mb-6 flex items-center justify-center gap-2 rounded-full bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700 ring-1 ring-emerald-100" data-testid="activation-paid-banner">
              <CheckCircle2 className="h-4 w-4" /> Payment received — your account is active
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h1 className="font-display text-2xl font-semibold tracking-tight text-slate-900">Set your password</h1>
              <p className="mt-1 text-sm text-slate-500">Create a password to access your {branding.productName} dashboard for <b>{info.business_name}</b>.</p>
              <form onSubmit={submitPassword} className="mt-6 space-y-4" data-testid="activation-password-form">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>First name</Label><Input value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="activation-first" /></div>
                  <div><Label>Last name</Label><Input value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="activation-last" /></div>
                </div>
                <div><Label>Create password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="activation-password" /></div>
                <div><Label>Confirm password</Label><Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="activation-confirm" /></div>
                <Button type="submit" className="w-full" disabled={busy} data-testid="activation-set-password-btn">
                  {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Activating…</> : <>Activate my account <ArrowRight className="ml-1 h-4 w-4" /></>}
                </Button>
              </form>
            </div>
          </div>
        ) : expired ? (
          <InvalidCard message="This activation link has expired. Please contact ResFil AI to have a new secure link sent to you." expired />
        ) : (
          /* ---------- Step 1: Review plan & pay ---------- */
          <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h1 className="font-display text-2xl font-semibold tracking-tight text-slate-900">Activate your {branding.productName} account</h1>
              <p className="mt-1 text-sm text-slate-500">Hi {info.first_name || "there"}, your account for <b>{info.business_name}</b> is ready. Review your approved plan below and complete secure payment to activate.</p>

              <div className="mt-6 rounded-xl border border-blue-100 bg-blue-50/60 p-4">
                <p className="flex items-center gap-1.5 text-xs font-semibold text-blue-800"><ShieldCheck className="h-3.5 w-3.5" /> Pay securely — no card over the phone</p>
                <p className="mt-1 text-xs text-slate-600">Payment is processed securely by Stripe. We never see or store your card details. After payment you'll set your own password to sign in.</p>
              </div>

              {info.disclosure_given === false && (
                <p className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="activation-awaiting-disclosure">
                  Your {branding.productName} representative needs to go through the terms with you before payment can be taken. They will do this with you on the phone.
                </p>
              )}
              <Button onClick={pay} className="mt-6 w-full" disabled={busy || info.disclosure_given === false} data-testid="activation-pay-btn">
                {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Redirecting to secure checkout…</> : <>Complete secure payment <ArrowRight className="ml-1 h-4 w-4" /></>}
              </Button>
              <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-400"><ShieldCheck className="h-3.5 w-3.5" /> Secured by Stripe · This link expires 72 hours after it was issued</p>
            </div>

            <aside className="h-fit rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="activation-summary">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Your approved plan</p>
              <p className="mt-1 font-display text-xl font-semibold text-slate-900" data-testid="activation-plan-name">{plan?.name ?? "—"}</p>
              {(() => {
                // An annual plan is a prepaid twelve-month term at its own price, with its own
                // setup fee and sometimes a discount. The figures come from the server so this page
                // and the charge can never disagree.
                const annual = plan?.billing_interval === "annual";
                const sub = plan?.subscription_price ?? plan?.monthly_price ?? 0;
                const setup = plan?.subscription_setup_fee ?? plan?.setup_fee ?? 0;
                const due = plan?.due_today ?? sub + setup;
                const list = plan?.list_subscription_price;
                const saved = list && list > sub ? list - sub : 0;
                return (
                  <div className="mt-4 space-y-2 text-sm">
                    <Row label={annual ? "Annual subscription (12 months)" : "Monthly subscription"}
                      value={annual ? money(sub) : `${money(sub)}/mo`} />
                    {saved > 0 && (
                      <div className="flex items-center justify-between text-xs text-emerald-700" data-testid="activation-discount">
                        <span>Discount applied</span>
                        <span>− {money(saved)} <span className="text-slate-400 line-through">{money(list)}</span></span>
                      </div>
                    )}
                    <Row label="One-time professional setup" value={money(setup)} />
                    <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-2">
                      <span className="font-medium text-slate-900">Due today</span>
                      <span className="font-semibold text-slate-900" data-testid="activation-due-today">{money(due)}</span>
                    </div>
                    <div className="border-t border-slate-100 pt-2 text-xs text-slate-500">
                      Taxes calculated at checkout.{" "}
                      {annual
                        ? `Covers twelve months of service and renews at ${money(list ?? sub)} a year. Refundable within 30 days of purchase.`
                        : `Renews at ${money(sub)}/month.`}
                    </div>
                  </div>
                );
              })()}
              <div className="mt-5 rounded-lg border border-slate-100 bg-slate-50 p-3">
                <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-700"><Sparkles className="h-3.5 w-3.5" /> What the setup fee includes</p>
                <p className="mt-1 text-xs text-slate-600">Configuration of your account, AI receptionist, business knowledge, scheduling rules, supported integrations, testing and launch — done for you by the ResFil team.</p>
              </div>
            </aside>
          </div>
        )}
      </main>
    </div>
  );
}

function InvalidCard({ message, expired }: { message: string; expired?: boolean }) {
  return (
    <div className="mx-auto max-w-md rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm" data-testid="activation-invalid">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-50 text-amber-600 ring-1 ring-amber-100">
        <AlertTriangle className="h-6 w-6" />
      </div>
      <h1 className="mt-4 font-display text-2xl font-semibold text-slate-900">{expired ? "Link expired" : "Link unavailable"}</h1>
      <p className="mx-auto mt-2 max-w-sm text-sm text-slate-500">{message}</p>
      <Link to="/" className="mt-6 inline-block text-sm font-medium text-blue-600 hover:text-blue-700" data-testid="activation-home">Back to homepage</Link>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return <div className="flex items-center justify-between"><span className="text-slate-500">{label}</span><span className="font-medium text-slate-900">{value}</span></div>;
}
